import type { ReactNode } from "react";
import { Calculator, Car, Home, Layers, type LucideIcon, ShieldCheck } from "lucide-react";

const RECURSOS: { icone: LucideIcon; texto: string }[] = [
  { icone: Layers, texto: "Estoque e precificação das cartas" },
  { icone: Calculator, texto: "Simulador e proposta em PDF" },
  { icone: ShieldCheck, texto: "Reservas de parceiros e do VictorIA PRO" },
];

/** Tela dividida do acesso (login, esqueci a senha, criar senha): marca à esquerda, formulário à direita. */
export function TelaAuth({ children }: { children: ReactNode }) {
  return (
    <div className="relative z-10 grid min-h-screen bg-white lg:grid-cols-2 xl:grid-cols-[55fr_45fr]">
      <PainelMarca />

      <div className="flex flex-col px-6 py-8 sm:px-10">
        <img src="/brand/tsi-logo.png" alt="TSI Soluções" className="h-9 w-fit lg:hidden" />
        <div className="flex flex-1 items-center justify-center py-10">{children}</div>
        <p className="text-center text-[12px] text-muted-foreground lg:hidden">© 2026 TSI Soluções · Cartas contempladas Servopa</p>
      </div>
    </div>
  );
}

function PainelMarca() {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-[linear-gradient(150deg,#0d3e58_0%,#0a3349_45%,#06283a_100%)] px-14 py-12 text-white lg:flex">
      {/* Profundidade: arcos concêntricos finos saindo do canto e um brilho verde quase imperceptível. */}
      <svg aria-hidden className="pointer-events-none absolute -bottom-[220px] -right-[220px] h-[760px] w-[760px] text-white" viewBox="0 0 760 760" fill="none">
        {[120, 200, 280, 360, 440, 520, 600].map((r, i) => (
          <circle key={r} cx="380" cy="380" r={r} stroke="currentColor" strokeOpacity={0.07 - i * 0.007} strokeWidth="1" />
        ))}
      </svg>
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[420px] rounded-full bg-[radial-gradient(circle,rgba(26,202,99,0.10),transparent_65%)]" />

      <img src="/brand/tsi-logo.png" alt="TSI Soluções" className="relative h-10 w-fit brightness-0 invert" />

      <div className="relative flex items-center gap-10">
        <div className="max-w-[440px] shrink-0">
          <div className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-white/60">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            Sistema interno
          </div>
          <h2 className="mt-4 text-[38px] font-semibold leading-[1.12] tracking-[-0.02em]">
            Cartas contempladas,
            <br />
            do estoque à reserva.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/65">Tudo o que a equipe da TSI precisa para vender cartas Servopa, num lugar só.</p>
          <ul className="mt-9 flex flex-col gap-4">
            {RECURSOS.map(({ icone: Icone, texto }) => (
              <li key={texto} className="flex items-center gap-3.5 text-[14.5px] text-white/85">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.07] ring-1 ring-white/10">
                  <Icone className="h-[17px] w-[17px] text-white/85" strokeWidth={1.6} />
                </span>
                {texto}
              </li>
            ))}
          </ul>
        </div>
        <PilhaDeCartas />
      </div>

      <div className="relative flex items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <span className="flex h-14 items-center rounded-xl bg-white px-4 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]">
            <img src="/brand/logo-servopa.png" alt="Consórcio Servopa, parceiro autorizado" className="h-10 w-auto" />
          </span>
        </div>
        <span className="text-[12px] text-white/45">© 2026 TSI Soluções</span>
      </div>
    </div>
  );
}

/** Ilustração discreta: duas cartas estilizadas (imóvel e automóvel), sem valores. Só em telas bem largas. */
function PilhaDeCartas() {
  return (
    <div aria-hidden className="hidden flex-1 justify-center 2xl:flex">
      <div className="relative h-[250px] w-[290px]">
        <CartaFake icone={Car} className="absolute right-0 top-0 rotate-[6deg]" />
        <CartaFake icone={Home} className="absolute bottom-0 left-0 rotate-[-4deg]" destaque />
      </div>
    </div>
  );
}

function CartaFake({ icone: Icone, className, destaque }: { icone: LucideIcon; className?: string; destaque?: boolean }) {
  return (
    <div
      className={`w-[240px] rounded-2xl p-5 ring-1 shadow-[0_28px_56px_-20px_rgba(0,0,0,0.55)] ${destaque ? "bg-white ring-white" : "bg-[#164a66] ring-white/10"} ${className ?? ""}`}
    >
      <div className="flex items-center justify-between">
        <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${destaque ? "bg-brand-50 text-brand" : "bg-white/10 text-white/80"}`}>
          <Icone className="h-[18px] w-[18px]" strokeWidth={1.7} />
        </span>
        <span className={`h-5 w-16 rounded-full ${destaque ? "bg-brand-green-50" : "bg-white/10"}`} />
      </div>
      <div className={`mt-5 h-2.5 w-24 rounded-full ${destaque ? "bg-brand-100" : "bg-white/15"}`} />
      <div className={`mt-3 h-4 w-36 rounded-full ${destaque ? "bg-brand" : "bg-white/25"}`} />
      <div className="mt-5 flex gap-2">
        <div className={`h-2 flex-1 rounded-full ${destaque ? "bg-brand-50" : "bg-white/10"}`} />
        <div className={`h-2 flex-1 rounded-full ${destaque ? "bg-brand-50" : "bg-white/10"}`} />
      </div>
    </div>
  );
}
